import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useSiteLayoutState } from "../../../hooks/useSiteLayoutState";
import { sdkAxiosInstance } from "../../../storefrontsdk";

export const FWQUERYBROWSER_DEFAULT_PAGESIZE = 20;

export class FwQueryResponse<TResponse> {
	Items: TResponse[] = [];
	PageNo: number = 1;
	PageSize: number = FWQUERYBROWSER_DEFAULT_PAGESIZE;
	TotalItems: number = 0;
	Sort: string = "";
}

export type FwQueryFilter<T> = {
	field: keyof T | "search";
	op:
		| "="
		| "<>"
		| "contains"
		| "does not contain"
		| "in"
		| "not in"
		| "starts with"
		| "ends with"
		| ">"
		| ">="
		| "<"
		| "<=";
	value: string;
};

// Helper function to convert to PascalCase - just uppercase the first character
// const toPascalCase = (str: string): string => {
//   // Handle empty strings
//   if (!str) return str;

//   // Just uppercase the first character and leave the rest unchanged
//   return str.charAt(0).toUpperCase() + str.slice(1);
//};

// Helper function to build the query URL
function buildQueryUrl<T>(
	baseurl: string,
	pageNo: number,
	pageSize: number,
	sort: string,
	filters: FwQueryFilter<T>[],
): string {
	const url = new URL(baseurl, window.location.origin);
	url.searchParams.append("pageno", pageNo.toString());
	url.searchParams.append("pagesize", pageSize.toString());

	// Add sort parameter if provided
	if (sort) {
		const sortParts = sort.split(" ");
		if (sortParts.length === 2) {
			const [field, direction] = sortParts;
			// const pascalField = toPascalCase(field);
			// url.searchParams.append('sort', `${pascalField} ${direction}`);
			url.searchParams.append("sort", `${field} ${direction}`);
		} else {
			url.searchParams.append("sort", sort);
		}
	}

	// Add filters if provided
	if (filters && filters.length > 0) {
		const pascalFilters = filters.map((filter) => ({
			...filter,
			//field: toPascalCase(filter.field)
			field: filter.field,
		}));
		url.searchParams.append("filter", JSON.stringify(pascalFilters));
	}

	return url.toString();
}

// Type for the query key
type FwQueryKey = [
	string, // baseurl
	number, // pageNo
	number, // pageSize
	string, // sort
	string, // stringified filters
];

// Interface for URL sync options
export interface UrlSyncOptions<T> {
	enabled: boolean;
	routePath?: string;
	initialFilters?: FwQueryFilter<T>[];
	paramMapping?: {
		pageNo?: string;
		pageSize?: string;
		sort?: string;
		filters?: string;
	};
}

// Default URL parameter names
const DEFAULT_PARAM_MAPPING = {
	pageNo: "page",
	pageSize: "size",
	sort: "sort",
	filters: "filters",
};

type NormalizedSearchSnapshot = Record<string, string>;

const searchParamsToSnapshot = (
	searchParams: URLSearchParams,
): NormalizedSearchSnapshot => {
	const snapshot: NormalizedSearchSnapshot = {};

	for (const [key, value] of searchParams.entries()) {
		snapshot[key] = value;
	}

	return snapshot;
};

const normalizeSearchSnapshot = (
	search: Record<string, unknown>,
): NormalizedSearchSnapshot => {
	return Object.keys(search).reduce((acc, key) => {
		const value = search[key];
		if (value === undefined || value === null) {
			return acc;
		}

		acc[key] = typeof value === "string" ? value : String(value);
		return acc;
	}, {} as NormalizedSearchSnapshot);
};

const areSnapshotsEqual = (
	a: NormalizedSearchSnapshot,
	b: NormalizedSearchSnapshot,
): boolean => {
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);

	if (keysA.length !== keysB.length) {
		return false;
	}

	for (const key of keysA) {
		if (a[key] !== b[key]) {
			return false;
		}
	}

	return true;
};

/**
 * React hook to track the state of GET endpoint that returns a FwQueryResponse<T>.
 * This provides functions to page through the data, change the filters, and get reactive changes
 * to the items and other properties like whether various buttons are disabled.
 *
 * Uses Tanstack React Query for efficient data fetching and caching.
 *
 * @param baseurl The base URL for the API endpoint
 * @param urlSync Optional configuration for URL synchronization
 * @returns FwQueryState<T> object with data and functions
 */
export function useFwQuery<T>(
	baseurl: string,
	urlSync: UrlSyncOptions<T> = { enabled: false },
): FwQueryState<T> {
	// Configure URL synchronization
	const syncEnabled = urlSync.enabled;
	const pageNoParam =
		urlSync.paramMapping?.pageNo ?? DEFAULT_PARAM_MAPPING.pageNo;
	const pageSizeParam =
		urlSync.paramMapping?.pageSize ?? DEFAULT_PARAM_MAPPING.pageSize;
	const sortParam = urlSync.paramMapping?.sort ?? DEFAULT_PARAM_MAPPING.sort;
	const filtersParam =
		urlSync.paramMapping?.filters ?? DEFAULT_PARAM_MAPPING.filters;
	const initialFiltersInput =
		(urlSync.initialFilters as FwQueryFilter<T>[] | undefined) ?? [];
	// biome-ignore lint/correctness/useExhaustiveDependencies: initialFiltersInput needs to be JSON.stringified for the code to work properly
	const initialFilters = useMemo(
		() => initialFiltersInput,
		[JSON.stringify(initialFiltersInput)],
	);

	// Get router hooks if URL sync is enabled
	const [searchParams, setSearchParams] = useSearchParams();
	const siteLayoutState = useSiteLayoutState();
	const urlSearch = useMemo(
		() => searchParamsToSnapshot(searchParams),
		[searchParams],
	);

	// Initialize state to defaults. Will be synced from URL if syncEnabled.
	const [pageNo, setPageNo] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(
		FWQUERYBROWSER_DEFAULT_PAGESIZE,
	);
	const [sort, setSort] = useState<string>("");
	const [filters, setFilters] = useState<FwQueryFilter<T>[]>(initialFilters);

	// State to control if the query is ready for its initial fetch,
	// especially when syncing from URL.
	const [isQueryReadyForFirstFetch, setIsQueryReadyForFirstFetch] =
		useState<boolean>(!syncEnabled);

	// State for UI elements
	const [url, setUrl] = useState<string>("");
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [firstBtnDisabled, setFirstBtnDisabled] = useState<boolean>(true);
	const [prevBtnDisabled, setPrevBtnDisabled] = useState<boolean>(true);
	const [nextBtnDisabled, setNextBtnDisabled] = useState<boolean>(true);
	const [lastBtnDisabled, setLastBtnDisabled] = useState<boolean>(true);
	const [pageNoBtnDisabled, setPageNoBtnDisabled] = useState<boolean>(true);
	const [pageSizeBtnDisabled, setPageSizeBtnDisabled] = useState<boolean>(true);

	// For tracking fetch status
	const fetchInProgress = useRef(false);

	// Ref to track if we're updating URL params to prevent loops
	const isUpdatingUrlRef = useRef(false);
	const hasCommittedInitialHistoryRef = useRef(false);
	const hasAppliedInitialUrlSyncRef = useRef(false);
	const lastAppliedSearchRef = useRef<NormalizedSearchSnapshot | null>(null);
	const shouldSkipUrlUpdateRef = useRef(false);
	const pageNoRef = useRef(pageNo);
	const pageSizeRef = useRef(pageSize);
	const sortRef = useRef(sort);
	const filtersRef = useRef(filters);

	pageNoRef.current = pageNo;
	pageSizeRef.current = pageSize;
	sortRef.current = sort;
	filtersRef.current = filters;

	// Get the React Query client
	const reactQueryClient = useQueryClient();

	// Create a unique query key based on current parameters
	const queryKey: FwQueryKey = [
		baseurl,
		pageNo,
		pageSize,
		sort,
		JSON.stringify(filters),
	];

	// Helper to try parsing filters from URL
	const tryParseFilters = useCallback(
		(filtersStr: unknown): FwQueryFilter<T>[] => {
			if (typeof filtersStr !== "string") return [];

			try {
				const parsed = JSON.parse(filtersStr);
				if (Array.isArray(parsed)) {
					// Ideally, you might want to add further validation here to ensure
					// each item in 'parsed' conforms to the FwQueryFilter<T> structure.
					return parsed as FwQueryFilter<T>[]; // Assuming parsed items are of the correct type
				}
			} catch (e) {
				console.error("Failed to parse filters from URL:", e);
			}

			return [];
		},
		[],
	); // Empty dependency array as it's a pure function of its arguments and doesn't close over hook state/props

	// Update URL if sync is enabled
	const updateUrl = useCallback(() => {
		if (!syncEnabled || isUpdatingUrlRef.current) return;

		const nextSearch: Record<string, string | number | undefined> = {};

		// Iterate over current urlSearch to carry over unmanaged params
		for (const key in urlSearch) {
			if (urlSearch[key] !== undefined && urlSearch[key] !== null) {
				// Ensure only known managed keys are NOT initially carried if they will be re-evaluated
				if (
					![pageNoParam, pageSizeParam, sortParam, filtersParam].includes(key)
				) {
					nextSearch[key] = String(urlSearch[key]);
				}
			}
		}

		// Page Number
		if (pageNo !== 1) {
			nextSearch[pageNoParam] = pageNo;
		} else {
			delete nextSearch[pageNoParam]; // Explicitly delete if default
		}

		// Page Size
		if (pageSize !== FWQUERYBROWSER_DEFAULT_PAGESIZE) {
			nextSearch[pageSizeParam] = pageSize;
		} else {
			delete nextSearch[pageSizeParam];
		}

		// Sort
		if (sort) {
			nextSearch[sortParam] = sort;
		} else {
			delete nextSearch[sortParam];
		}

		// Filters
		if (filters.length > 0) {
			nextSearch[filtersParam] = JSON.stringify(filters);
		} else {
			delete nextSearch[filtersParam];
		}

		const normalizedNextSearch = normalizeSearchSnapshot(
			nextSearch as Record<string, unknown>,
		);
		const normalizedCurrentSearch = normalizeSearchSnapshot(urlSearch);
		const matchesLastApplied =
			lastAppliedSearchRef.current !== null &&
			areSnapshotsEqual(lastAppliedSearchRef.current, normalizedNextSearch);
		const hasChanges = !areSnapshotsEqual(
			normalizedCurrentSearch,
			normalizedNextSearch,
		);
		const shouldReplace = !hasCommittedInitialHistoryRef.current;

		if (shouldSkipUrlUpdateRef.current) {
			shouldSkipUrlUpdateRef.current = false;
			if (!hasCommittedInitialHistoryRef.current) {
				hasCommittedInitialHistoryRef.current = true;
			}
			return;
		}

		if (hasChanges && !matchesLastApplied) {
			isUpdatingUrlRef.current = true;
			try {
				lastAppliedSearchRef.current = normalizedNextSearch;
				const nextSearchParams = new URLSearchParams();

				for (const [key, value] of Object.entries(nextSearch)) {
					if (value !== undefined) {
						nextSearchParams.set(key, String(value));
					}
				}

				setSearchParams(nextSearchParams, { replace: shouldReplace });
			} finally {
				isUpdatingUrlRef.current = false;
			}
		}

		if (!hasCommittedInitialHistoryRef.current) {
			hasCommittedInitialHistoryRef.current = true;
		}
	}, [
		syncEnabled,
		pageNo,
		pageSize,
		sort,
		filters,
		//navigate,
		pageNoParam,
		pageSizeParam,
		sortParam,
		filtersParam,
		urlSearch,
		setSearchParams,
	]);

	// Effect to synchronize state from URL when urlSearch changes or on initial load
	useLayoutEffect(() => {
		if (syncEnabled) {
			shouldSkipUrlUpdateRef.current = true;
			const normalizedCurrentSearch = normalizeSearchSnapshot(urlSearch);
			lastAppliedSearchRef.current = normalizedCurrentSearch;

			// If updateUrl is currently pushing state to the URL, avoid a feedback loop.
			if (isUpdatingUrlRef.current) {
				return;
			}

			const latestUrlPageNo = urlSearch?.[pageNoParam]
				? Number(urlSearch[pageNoParam])
				: 1;
			const latestUrlPageSize = urlSearch?.[pageSizeParam]
				? Number(urlSearch[pageSizeParam])
				: FWQUERYBROWSER_DEFAULT_PAGESIZE;
			const latestUrlSort = urlSearch?.[sortParam]
				? String(urlSearch[sortParam])
				: "";
			const hasFiltersInUrl = Boolean(urlSearch?.[filtersParam]);
			const latestUrlFilters = hasFiltersInUrl
				? tryParseFilters(urlSearch[filtersParam])
				: hasAppliedInitialUrlSyncRef.current
					? []
					: initialFilters;

			// Update internal state only if it differs from what the URL indicates.
			if (pageNoRef.current !== latestUrlPageNo) {
				setPageNo(latestUrlPageNo);
			}
			if (pageSizeRef.current !== latestUrlPageSize) {
				setPageSize(latestUrlPageSize);
			}
			if (sortRef.current !== latestUrlSort) {
				setSort(latestUrlSort);
			}
			if (
				JSON.stringify(filtersRef.current) !== JSON.stringify(latestUrlFilters)
			) {
				setFilters(latestUrlFilters);
			}

			hasAppliedInitialUrlSyncRef.current = true;

			// After the first attempt to sync from URL (i.e., this effect runs),
			// the query is considered ready to make its first fetch with synced params.
			if (!isQueryReadyForFirstFetch) {
				setIsQueryReadyForFirstFetch(true);
			}
		} else {
			lastAppliedSearchRef.current = null;
			shouldSkipUrlUpdateRef.current = false;
			hasAppliedInitialUrlSyncRef.current = false;
			// If sync is not enabled, ensure the query is marked as ready.
			if (!isQueryReadyForFirstFetch) {
				setIsQueryReadyForFirstFetch(true);
			}
		}
		// This effect depends on urlSearch to react to URL changes (initial load, back/fwd).
		// It also depends on syncEnabled and paramMapping for configuration.
		// It intentionally does not depend on pageNo, pageSize, etc., to avoid loops,
		// as the conditional setStates (e.g., pageNo !== latestUrlPageNo) prevent cycles.
		// isQueryReadyForFirstFetch is included to ensure it runs if it was false and needs to become true.
		/* eslint-disable react-hooks/exhaustive-deps */
	}, [
		syncEnabled,
		urlSearch,
		pageNoParam,
		pageSizeParam,
		sortParam,
		filtersParam,
		initialFilters,
		isQueryReadyForFirstFetch,
		tryParseFilters,
	]);

	// Effect to update URL when state changes if sync is enabled
	useEffect(() => {
		if (isQueryReadyForFirstFetch) {
			// Only attempt to update URL if we are past initial sync from URL
			updateUrl();
		}
	}, [isQueryReadyForFirstFetch, updateUrl]); // updateUrl callback's dependencies cover pageNo, pageSize, etc.

	// Helper for updating button states
	const updateButtonStates = useCallback(
		(data?: FwQueryResponse<T>) => {
			const totalItems = data?.TotalItems || 0;
			const totalPages = Math.ceil(totalItems / pageSize) || 0;

			setFirstBtnDisabled(pageNo <= 1);
			setPrevBtnDisabled(pageNo <= 1);
			setNextBtnDisabled(pageNo >= totalPages);
			setLastBtnDisabled(pageNo >= totalPages);
			setPageNoBtnDisabled(false);
			setPageSizeBtnDisabled(false);
		},
		[pageNo, pageSize],
	);

	// Use React Query for data fetching
	const {
		data: queryResult,
		isLoading,
		isError,
		isFetching,
		error,
		refetch,
	} = useQuery<FwQueryResponse<T>, Error>({
		queryKey,
		queryFn: async () => {
			//console.log('⚡ Fetching data for:', baseurl);

			if (!baseurl) {
				throw new Error("No base URL provided");
			}

			// Build the full query URL
			const fullUrl = buildQueryUrl(baseurl, pageNo, pageSize, sort, filters);
			setUrl(fullUrl);
			//console.log('🌐 Request URL:', fullUrl);

			// Fetch the data
			fetchInProgress.current = true;
			try {
				const response =
					await sdkAxiosInstance.get<FwQueryResponse<T>>(fullUrl);

				if (response.status === 401) {
					console.log("🔒 Unauthorized access - logging out user");
					siteLayoutState.redirectToLogin();
					throw new Error("Session expired. Please log in again.");
				}

				//if (response.status !== 200) {
				//  //throw new Error(`API Error (${response.status}): ${response.data ?? ''}`);
				//  setErrorMessage(`API Error (${response.status}): ${response.data ?? ''}`);
				//}

				//console.log('✅ Data received:', response.data.items.length, 'items');
				if (response.status === 200) {
					return response.data;
				}

				throw new Error("Unable to load data.");
			} catch (error) {
				if (
					error instanceof AxiosError &&
					(error.response?.status === 401 || error.response?.status === 403)
				) {
					siteLayoutState.redirectToLogin();
					throw new Error("Session expired. Please log in again.");
				}
				throw error;
			} finally {
				fetchInProgress.current = false;
			}
		},
		enabled: isQueryReadyForFirstFetch && !!baseurl, // Query only runs when ready and baseurl is present
		staleTime: 0, // Always mark data as stale immediately (will refetch on next render)
		gcTime: 5 * 60 * 1000, // Keep data in cache for 5 minutes
		retry: (failureCount, error) => {
			// Don't retry on 403 errors
			if (error.message.includes("Session expired")) {
				return false;
			}
			// Only retry once for other errors
			return failureCount < 1;
		},
		refetchOnWindowFocus: false, // Don't refetch when window regains focus
	});

	// Extract data from query result or provide defaults
	const data = queryResult;
	const items = data?.Items || [];
	const totalItems = data?.TotalItems || 0;
	const totalPages = Math.ceil(totalItems / pageSize) || 0;

	// Set error message if there's an error
	useEffect(() => {
		if (isError && error) {
			setErrorMessage(error.message);
		} else {
			setErrorMessage("");
		}
	}, [isError, error]);

	// Update button states whenever data changes
	useEffect(() => {
		updateButtonStates(data);
	}, [data, updateButtonStates]);

	// Function to reset all state
	const reset = useCallback(() => {
		setPageNo(1);
		setPageSize(FWQUERYBROWSER_DEFAULT_PAGESIZE);
		setSort("");
		setFilters([]);
		setUrl("");
		setErrorMessage("");
	}, []);

	// Function to manually trigger refetch
	const loadAsync = useCallback(async (): Promise<void> => {
		if (fetchInProgress.current) {
			//console.log('Fetch already in progress, skipping request');
			return;
		}

		try {
			//console.log('Manually triggering data refresh');
			await refetch();
		} catch (error) {
			console.error("Error refreshing data:", error);
			setErrorMessage(
				error instanceof Error ? error.message : "An unknown error occurred",
			);
		}
	}, [refetch]);

	// Navigation functions
	const firstPageAsync = useCallback(async (): Promise<void> => {
		if (!firstBtnDisabled && pageNo > 1) {
			setPageNo(1);
		}
	}, [firstBtnDisabled, pageNo]);

	const prevPageAsync = useCallback(async (): Promise<void> => {
		if (!prevBtnDisabled && pageNo > 1) {
			setPageNo((prev) => prev - 1);
		}
	}, [prevBtnDisabled, pageNo]);

	const nextPageAsync = useCallback(async (): Promise<void> => {
		if (!nextBtnDisabled) {
			if (pageNo > totalPages) {
				setPageNo(totalPages);
			} else if (pageNo >= 1 && pageNo < totalPages) {
				setPageNo((prev) => prev + 1);
			} else if (pageNo < 1) {
				setPageNo(1);
			}
		}
	}, [nextBtnDisabled, pageNo, totalPages]);

	const lastPageAsync = useCallback(async (): Promise<void> => {
		if (!lastBtnDisabled && pageNo < totalPages) {
			setPageNo(totalPages);
		}
	}, [lastBtnDisabled, pageNo, totalPages]);

	const setPageNoAsync = useCallback(
		async (
			newPageNo: number,
			overrideAllowSetPageNo: boolean = false,
		): Promise<void> => {
			if (
				(!pageNoBtnDisabled || overrideAllowSetPageNo) &&
				newPageNo !== pageNo
			) {
				if (newPageNo === 1) {
					setPageNo(newPageNo);
				} else if (newPageNo > 1 && data) {
					if (newPageNo >= 1 && newPageNo <= totalPages) {
						setPageNo(newPageNo);
					}
				}
			}
		},
		[pageNoBtnDisabled, pageNo, data, totalPages],
	);

	const setPageSizeAsync = useCallback(
		async (newPageSize: number): Promise<void> => {
			if (!pageSizeBtnDisabled && newPageSize !== pageSize) {
				setPageNo(1);
				setPageSize(newPageSize);
			}
		},
		[pageSizeBtnDisabled, pageSize],
	);

	// Filter and sort functions
	const setFiltersAsync = useCallback(
		async (newFilters: FwQueryFilter<T>[]): Promise<void> => {
			//console.log('Setting filters to:', newFilters);

			// Show Pascal case conversion for debugging
			// if (newFilters.length > 0) {
			//   const pascalFilters = newFilters.map(filter => ({
			//     ...filter,
			//     field: toPascalCase(filter.field)
			//   }));
			//   console.log('Pascal case filters:', pascalFilters);
			// }

			// Update filters and reset to page 1
			setFilters(newFilters);
			setPageNo(1);

			// Invalidate React Query cache for this query to force a refetch
			const cacheKey = [baseurl, 1, pageSize, sort, JSON.stringify(newFilters)];
			reactQueryClient.invalidateQueries({ queryKey: cacheKey });
		},
		[baseurl, pageSize, sort, reactQueryClient],
	);

	const setSortAsync = useCallback(
		async (newSort: string): Promise<void> => {
			//console.log('⭐ Setting sort state to:', newSort);

			// Parse the sort string to extract field and direction
			const sortParts = newSort.split(" ");
			//let field = '';
			//let direction = '';

			if (sortParts.length === 2) {
				// [field, direction] = sortParts;
				// const pascalField = toPascalCase(field);
				//console.log(`Sort field: "${field}", PascalCase: "${pascalField}", direction: "${direction}"`);
			} else {
				console.warn("Invalid sort string format:", newSort);
				return; // Don't proceed with invalid format
			}

			// Update sort state
			setSort(newSort);

			// Invalidate React Query cache for this query to force a refetch
			const cacheKey = [
				baseurl,
				pageNo,
				pageSize,
				newSort,
				JSON.stringify(filters),
			];
			reactQueryClient.invalidateQueries({ queryKey: cacheKey });
		},
		[baseurl, pageNo, pageSize, filters, reactQueryClient],
	);

	return {
		// Props
		pageNo,
		pageSize,
		filters,
		sort,
		isPending: isLoading,
		isFetching,
		errorMessage,
		items,
		totalItems,
		totalPages,
		url,
		firstBtnDisabled,
		prevBtnDisabled,
		nextBtnDisabled,
		lastBtnDisabled,
		pageNoBtnDisabled,
		pageSizeBtnDisabled,

		// Functions
		reset,
		loadAsync,
		firstPageAsync,
		prevPageAsync,
		nextPageAsync,
		lastPageAsync,
		setPageNoAsync,
		setPageSizeAsync,
		setFiltersAsync,
		setSortAsync,
	};
}

export type FwQueryState<T> = {
	// Props
	pageNo: number;
	pageSize: number;
	filters: FwQueryFilter<T>[];
	sort: string;
	isPending: boolean;
	isFetching: boolean;
	errorMessage: string;
	items: T[];
	totalItems: number;
	totalPages: number;
	url: string;
	firstBtnDisabled: boolean;
	prevBtnDisabled: boolean;
	nextBtnDisabled: boolean;
	lastBtnDisabled: boolean;
	pageNoBtnDisabled: boolean;
	pageSizeBtnDisabled: boolean;

	// Functions
	reset: () => void;
	loadAsync: () => Promise<void>;
	firstPageAsync: () => Promise<void>;
	prevPageAsync: () => Promise<void>;
	nextPageAsync: () => Promise<void>;
	lastPageAsync: () => Promise<void>;
	setPageNoAsync: (
		pageNo: number,
		overrideAllowSetPageNo?: boolean,
	) => Promise<void>;
	setPageSizeAsync: (pageSize: number) => Promise<void>;
	setFiltersAsync: (filters: FwQueryFilter<T>[]) => Promise<void>;
	setSortAsync: (sort: string) => Promise<void>;
};
